import { ListingWithComp } from 'src/tiles/ListingTilesV2';
import {
  getFormattedEventName,
  getPerformerAndVenueForEvent,
} from 'src/utils/eventWithDataUtils';
import { sortSeating } from 'src/utils/tableUtils';
import {
  CatalogResults,
  EventWithData,
  ListingMetrics,
  ListingNoteType,
  SaleMetrics,
  TicketGroupMetrics,
} from 'src/WebApiController';

import {
  defaultEventSortingCriteria,
  defaultListingSortingCriteria,
} from './MobileInventorySortAndFilterContext.constants';
import {
  EventSortByFields,
  EventSortDisplayConfig,
  IneventorySortingType,
  InventoryEventSorting,
  InventoryListingSorting,
  ListingSortByFields,
  ListingSortDisplayConfig,
} from './MobileInventorySortAndFilterContext.types';

const isDefeatsListing = (listing: ListingWithComp): boolean => {
  return (listing.listingNotes ?? []).some(
    (note) =>
      note.type === ListingNoteType.Defect ||
      note.type === ListingNoteType.RestrictionOnUse
  );
};

const compareByDateStr = (d1?: string | null, d2?: string | null) => {
  if (!d1 || !d2) {
    return 0;
  }

  const aStart = d1 ? new Date(d1) : new Date();
  const bStart = d2 ? new Date(d2) : new Date();
  return aStart < bStart ? -1 : aStart === bStart ? 0 : 1;
};

const compareByStr = (s1?: string | null, s2?: string | null) => {
  if (!s1 || !s2) {
    return 0;
  }
  return s1.localeCompare(s2);
};

const compareByNumber = (n1?: number | null, n2?: number | null) => {
  if (!n1 || !n2) {
    return 0;
  }
  return n1 - n2;
};

export const getDefaultSortingCriteria = (type: IneventorySortingType) => {
  if (type === IneventorySortingType.Event) {
    return defaultEventSortingCriteria;
  }
  return defaultListingSortingCriteria;
};

export const filterListingByDisplayConfig =
  (configs: ListingSortDisplayConfig[]) => (listing: ListingWithComp) => {
    if (listing.isCompListing) {
      if (configs.includes(ListingSortDisplayConfig.HideAllDefects)) {
        return !isDefeatsListing(listing);
      }
      if (configs.includes(ListingSortDisplayConfig.HideAllFanInventory)) {
        return !listing.isFanInventory;
      }
    } else {
      if (configs.includes(ListingSortDisplayConfig.HideSoldInventory)) {
        return listing.availQty !== 0;
      }
    }
    return true;
  };

export const filterEventByDisplayConfig =
  (
    configs: EventSortDisplayConfig[],
    eventMetrics?: Record<string, ListingMetrics>
  ) =>
  (event: EventWithData) => {
    const eventListingMetrics = eventMetrics?.[event.event?.virtId];
    if (
      eventListingMetrics &&
      configs.includes(EventSortDisplayConfig.HideSoldThrough)
    ) {
      return eventListingMetrics.tktQty !== eventListingMetrics.soldQty;
    }

    return true;
  };

export const sortListingWithCriteria =
  ({ sortBy, order }: InventoryListingSorting, allInventory?: boolean) =>
  (l1: ListingWithComp, l2: ListingWithComp) => {
    // All inventory are mapped to all in prices and we want to sort it only by price
    if (allInventory) {
      return l1.allInPrice - l2.allInPrice;
    }
    const sign = order === 'asc' ? 1 : -1;
    if (sortBy === ListingSortByFields.Quantity) {
      return sign * (l1.availQty - l2.availQty);
    }
    if (sortBy === ListingSortByFields.UnitCost) {
      return sign * ((l1.unitCst?.amt ?? 0) - (l2.unitCst?.amt ?? 0));
    }
    if (sortBy === ListingSortByFields.Seating) {
      return sign * sortSeating(l1.seating, l2.seating);
    }
    return (
      sign *
      ((l1.listPrice ?? l1.unitCst?.amt ?? 0) -
        (l2.listPrice ?? l2.unitCst?.amt ?? 0))
    );
  };

export const sortEventWithCriteria =
  (
    { sortBy, order }: InventoryEventSorting,
    eventMetrics?: Record<
      string,
      ListingMetrics | TicketGroupMetrics | SaleMetrics
    >,
    catalogResult?: CatalogResults | undefined
  ) =>
  ({ event: e1 }: EventWithData, { event: e2 }: EventWithData) => {
    const sign = order === 'asc' ? 1 : -1;

    if (!e1 || !e2) {
      return 0;
    }
    if (sortBy === EventSortByFields.UnsoldListings) {
      const m1 = eventMetrics?.[e1.virtId] as ListingMetrics;
      const m2 = eventMetrics?.[e2.virtId] as ListingMetrics;
      return sign * compareByNumber(m1?.unsoldQty, m2?.unsoldQty);
    }
    if (sortBy === EventSortByFields.TicketCount) {
      const m1 = eventMetrics?.[e1.virtId] as TicketGroupMetrics;
      const m2 = eventMetrics?.[e2.virtId] as TicketGroupMetrics;
      return sign * compareByNumber(m1?.tktQty, m2?.tktQty);
    }

    const { venue: v1, performer: p1 } = getPerformerAndVenueForEvent(
      e1,
      catalogResult
    );
    const { venue: v2, performer: p2 } = getPerformerAndVenueForEvent(
      e2,
      catalogResult
    );
    if (sortBy === EventSortByFields.EventName) {
      const [en1] = getFormattedEventName({
        event: e1,
        performer: p1,
      });
      const [en2] = getFormattedEventName({
        event: e2,
        performer: p2,
      });
      return sign * compareByStr(en1, en2);
    }
    if (sortBy === EventSortByFields.VenueName) {
      return sign * compareByStr(v1?.name, v2?.name);
    }

    // Default with date
    return sign * compareByDateStr(e1?.dates?.start, e2?.dates?.start);
  };

export const toQuantityFilterString = (listing: ListingWithComp) => {
  if (!listing.availQty) {
    return '0';
  }
  if (listing.availQty >= 10) {
    return '10+';
  }
  return listing.availQty + '';
};
